import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, auth as authApi } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { isCrossHost, safeRedirect } from "@/lib/safeRedirect";

/**
 * Login page — three sign-in paths, in priority order:
 *
 *   1. Email + password (primary form — most familiar pattern)
 *   2. Google sign-in (Google Identity Services button below the form)
 *   3. Paste-JWT fallback (collapsed; still useful for debugging and as a
 *      recovery path while we sort out per-environment Google client IDs)
 *
 * Email + password is proxied via our backend to Skarp's `/api/auth/login`,
 * just like the Google flow proxies to Skarp's `/api/auth/google`. On success,
 * `authApi.emailLogin` returns and the AuthContext flips to "authenticated";
 * the redirect-back-to-where useEffect at the bottom takes the user to
 * wherever they were heading (or `/`).
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            ux_mode?: "popup" | "redirect";
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }) => void;
          renderButton: (
            parent: HTMLElement,
            options: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              text?: "signin_with" | "signup_with" | "continue_with" | "signin";
              shape?: "rectangular" | "pill" | "circle" | "square";
              logo_alignment?: "left" | "center";
              width?: number | string;
            },
          ) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

function loadGsiScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google?.accounts?.id) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("gsi load failed")), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("gsi load failed"));
    document.head.appendChild(s);
  });
}

export function Login() {
  const { refresh, state } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = safeRedirect(searchParams.get("from"));

  // Email + password (primary)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Forgot-password sub-form (toggle)
  const [showForgot, setShowForgot] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);

  // Paste-JWT fallback (collapsed by default)
  const [showJwtFallback, setShowJwtFallback] = useState(false);
  const [token, setToken] = useState("");
  const [jwtBusy, setJwtBusy] = useState(false);
  const [jwtError, setJwtError] = useState<string | null>(null);

  // Google sign-in
  const [googleClientId, setGoogleClientId] = useState<string | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  // Once authenticated, redirect-back-to-where. React-router's `navigate`
  // only handles same-host paths; for the OAuth shim flow the user came in
  // from `coauthor.skarp.app/oauth/authorize?...` which is a cross-host
  // absolute URL, so we use `window.location.href` for those.
  useEffect(() => {
    if (state.status === "authenticated") {
      if (isCrossHost(redirectTo)) {
        window.location.href = redirectTo;
      } else {
        navigate(redirectTo, { replace: true });
      }
    }
  }, [state.status, navigate, redirectTo]);

  // Fetch the runtime config to know whether Google sign-in is enabled.
  useEffect(() => {
    let cancelled = false;
    void authApi
      .config()
      .then((cfg) => {
        if (cancelled) return;
        setGoogleClientId(cfg.google_client_id);
      })
      .catch(() => {
        // Silent — auth config is best-effort; email/JWT still work.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load the GSI script + render the Sign-In button once we have a client id.
  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled) return;
        const g = window.google?.accounts?.id;
        if (!g) {
          setGoogleError("Google sign-in could not initialize.");
          return;
        }
        g.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            const credential = response.credential;
            if (!credential) {
              setGoogleError("Google did not return a credential.");
              return;
            }
            setGoogleBusy(true);
            setGoogleError(null);
            try {
              await authApi.google(credential);
              await refresh();
            } catch (e) {
              if (e instanceof ApiError) {
                const body = e.body as { error?: string } | null;
                setGoogleError(body?.error ?? `Google sign-in failed (${e.status})`);
              } else {
                setGoogleError(String(e));
              }
            } finally {
              setGoogleBusy(false);
            }
          },
          ux_mode: "popup",
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        if (buttonRef.current) {
          buttonRef.current.innerHTML = "";
          g.renderButton(buttonRef.current, {
            type: "standard",
            theme: "outline",
            size: "large",
            text: "signin_with",
            shape: "rectangular",
            logo_alignment: "left",
            width: 320,
          });
        }
        setGoogleReady(true);
      })
      .catch((e) => {
        setGoogleError(`Google sign-in unavailable: ${String(e)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [googleClientId, refresh]);

  const onEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    if (!email.trim() || !password) {
      setEmailError("Email and password are required.");
      return;
    }
    setEmailBusy(true);
    try {
      await authApi.emailLogin(email.trim(), password);
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setEmailError(body?.error ?? `Sign-in failed (${err.status})`);
      } else {
        setEmailError(String(err));
      }
    } finally {
      setEmailBusy(false);
    }
  };

  const onJwtSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setJwtError(null);
    if (!token.trim()) {
      setJwtError("Paste a Skarp JWT to continue.");
      return;
    }
    setJwtBusy(true);
    try {
      await authApi.skarpCallback(token.trim());
      await refresh();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setJwtError(body?.error ?? `auth failed (${err.status})`);
      } else {
        setJwtError(String(err));
      }
    } finally {
      setJwtBusy(false);
    }
  };

  const onForgot = async () => {
    setForgotMessage(null);
    if (!email.trim()) {
      setForgotMessage("Enter your email first.");
      return;
    }
    setForgotBusy(true);
    try {
      const r = await authApi.forgotPassword(email.trim());
      setForgotMessage(r.message);
    } catch {
      // The endpoint always returns ok in normal operation; surface a generic
      // message if the request itself failed.
      setForgotMessage("If that email is registered, a reset link is on its way.");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-zinc-950">
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-semibold tracking-tight text-lg">
            ← Coauthor
          </Link>
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <h1 className="text-2xl font-semibold mb-2">Sign in</h1>
          <p className="text-sm text-zinc-500 mb-6">
            Use your Skarp account to start writing documents with AI agents.
          </p>

          {/* Email + password — primary */}
          <form onSubmit={onEmailSubmit} className="space-y-3 mb-6">
            <label className="block">
              <span className="text-sm font-medium">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                placeholder="you@example.com"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm"
              />
            </label>
            {emailError && (
              <div className="text-sm text-red-600 dark:text-red-400">{emailError}</div>
            )}
            <div className="flex items-center justify-between gap-3">
              <button
                type="submit"
                disabled={emailBusy}
                className="inline-flex items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
              >
                {emailBusy ? "Signing in…" : "Sign in"}
              </button>
              <button
                type="button"
                onClick={() => setShowForgot((v) => !v)}
                className="text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 underline"
              >
                Forgot password?
              </button>
            </div>
          </form>

          {showForgot && (
            <div className="rounded-md border border-zinc-200 dark:border-zinc-800 p-3 mb-6 bg-zinc-50 dark:bg-zinc-900/40">
              <p className="text-xs text-zinc-600 dark:text-zinc-400 mb-2">
                We'll send a reset link to the email above.
              </p>
              <button
                type="button"
                onClick={onForgot}
                disabled={forgotBusy}
                className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-white dark:hover:bg-zinc-900 disabled:opacity-50"
              >
                {forgotBusy ? "Sending…" : "Send reset link"}
              </button>
              {forgotMessage && (
                <div className="text-xs text-emerald-700 dark:text-emerald-400 mt-2">
                  {forgotMessage}
                </div>
              )}
            </div>
          )}

          {/* Google sign-in */}
          {googleClientId && (
            <>
              <div className="flex items-center gap-3 my-6 text-xs text-zinc-500 uppercase tracking-wide">
                <span className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
                <span>or</span>
                <span className="flex-1 h-px bg-zinc-200 dark:bg-zinc-800" />
              </div>
              <div className="mb-6">
                <div ref={buttonRef} className="flex justify-start min-h-[44px]" />
                {!googleReady && !googleError && (
                  <div className="text-xs text-zinc-500 mt-1">Loading Google sign-in…</div>
                )}
                {googleError && (
                  <div className="text-sm text-red-600 dark:text-red-400 mt-2">{googleError}</div>
                )}
                {googleBusy && (
                  <div className="text-xs text-zinc-500 mt-2">Verifying with Google…</div>
                )}
              </div>
            </>
          )}

          {/* Footer — register + paste-JWT */}
          <div className="border-t border-zinc-200 dark:border-zinc-800 pt-4 text-sm text-zinc-500 space-y-2">
            <div>
              Don't have an account?{" "}
              <Link
                to={`/register${redirectTo !== "/" ? `?from=${encodeURIComponent(redirectTo)}` : ""}`}
                className="text-zinc-900 dark:text-zinc-100 underline hover:opacity-80"
              >
                Create one →
              </Link>
            </div>
            <div className="text-xs">
              <button
                type="button"
                onClick={() => setShowJwtFallback((v) => !v)}
                className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 underline"
              >
                {showJwtFallback ? "Hide" : "Show"} developer / paste-JWT login
              </button>
            </div>
          </div>

          {showJwtFallback && (
            <form onSubmit={onJwtSubmit} className="mt-4 space-y-3 rounded-md border border-zinc-200 dark:border-zinc-800 p-3 bg-zinc-50 dark:bg-zinc-900/40">
              <label className="block">
                <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
                  Skarp JWT
                </span>
                <textarea
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  rows={3}
                  spellCheck={false}
                  placeholder="eyJhbGciOiJIUzI1NiIs..."
                  className="mt-1 block w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-2 font-mono text-xs"
                />
              </label>
              {jwtError && (
                <div className="text-xs text-red-600 dark:text-red-400">{jwtError}</div>
              )}
              <button
                type="submit"
                disabled={jwtBusy}
                className="text-xs rounded-md border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 hover:bg-white dark:hover:bg-zinc-900 disabled:opacity-50"
              >
                {jwtBusy ? "Verifying…" : "Sign in with JWT"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
